import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, ClockCounterClockwise, SignIn, UserPlus, UserMinus, PencilSimple, BookOpen, Trash, UsersThree, ClipboardText, CaretLeft, CaretRight } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";

const ACTION_META = {
  connexion: { label: "Connexion", icon: SignIn, color: "bg-secondary text-foreground" },
  creation_utilisateur: { label: "Création utilisateur", icon: UserPlus, color: "bg-primary/10 text-primary" },
  modification_utilisateur: { label: "Modification utilisateur", icon: PencilSimple, color: "bg-primary/10 text-primary" },
  suppression_utilisateur: { label: "Suppression utilisateur", icon: UserMinus, color: "bg-accent/10 text-accent" },
  creation_cours: { label: "Création cours", icon: BookOpen, color: "bg-primary/10 text-primary" },
  modification_cours: { label: "Modification cours", icon: PencilSimple, color: "bg-primary/10 text-primary" },
  suppression_cours: { label: "Suppression cours", icon: Trash, color: "bg-accent/10 text-accent" },
  creation_groupe: { label: "Création groupe", icon: UsersThree, color: "bg-primary/10 text-primary" },
  suppression_groupe: { label: "Suppression groupe", icon: Trash, color: "bg-accent/10 text-accent" },
  creation_devoir: { label: "Création devoir", icon: ClipboardText, color: "bg-primary/10 text-primary" },
  suppression_devoir: { label: "Suppression devoir", icon: Trash, color: "bg-accent/10 text-accent" },
  notation: { label: "Notation", icon: ClipboardText, color: "bg-[hsl(var(--campus-green))]/10 text-[hsl(var(--campus-green))]" },
};
const PAGE_SIZE = 25;

export default function JournalActivite() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const load = (p) => {
    setLoading(true);
    const params = { page: p, page_size: PAGE_SIZE };
    if (actionFilter !== "all") params.action = actionFilter;
    if (search.trim()) params.q = search.trim();
    api.get("/admin/activity-log", { params })
      .then((r) => { setLogs(r.data.items); setTotal(r.data.total); setTotalPages(r.data.total_pages); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  // recherche/filtre -> retour à la page 1, avec un léger debounce sur la recherche
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1); }, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, actionFilter]);

  useEffect(() => { load(page); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Journal d'activité</h1>
        <p className="text-sm text-muted-foreground">Historique des connexions, créations, modifications et suppressions sur la plateforme.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher une personne ou une action..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="log-search" />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-56" data-testid="log-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            {Object.entries(ACTION_META).map(([key, meta]) => (
              <SelectItem key={key} value={key}>{meta.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!loading && logs.length === 0 ? (
        <EmptyState icon={ClockCounterClockwise} title="Aucune activité" description="Rien à afficher pour ce filtre." />
      ) : (
        <>
          <div className="space-y-2">
            {logs.map((l) => {
              const meta = ACTION_META[l.action] || { label: l.action, icon: ClockCounterClockwise, color: "bg-secondary text-foreground" };
              const Icon = meta.icon;
              return (
                <div key={l.id} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4" data-testid={`log-row-${l.id}`}>
                  <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.color}`}>
                    <Icon size={18} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="text-[10px]">{meta.label}</Badge>
                      <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
                    </div>
                    <p className="mt-1 text-sm">{l.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">
              {total} entrée{total > 1 ? "s" : ""} · page {page} / {totalPages}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} data-testid="log-prev-page">
                <CaretLeft size={14} className="mr-1" /> Précédent
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} data-testid="log-next-page">
                Suivant <CaretRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
